// profitWhatIf.js - Insight popup (2026-10-26, owner): what-if discount + Forward/Net profit per piece.
// Pure, read-only, no I/O. Prices with profitCalcClient.calcRow (exact port of backend/profitCalc.calcRow, the engine behind
// POST /api/profit/calc) on the same profit_settings, so a what-if at the style's own discount equals the server tile.
import { calcRow, ALL_CH, normCat, suggestDiscount } from "./profitCalcClient";

// Suggested discount = the SAME engine as the Styles table "Suggest Disc" (profitCalcClient.suggestDiscount): lowers the current discount in 0.5% steps
// until the benchmark channel reaches the target margin (profit / SP ex-tax). Same defaults as that table (8%, Myntra). null = already at/above target, or
// unreachable even at 0% discount, or the channel is not priced for this style.
export const SUGGEST_TARGET_PCT = 8;
export const SUGGEST_CHANNEL = "myntra";

export const MIN_SOLD_FOR_OWN_RATE = 10;   // fewer pieces sold in the window than this -> the channel's own return % is too thin to trust

// Rebuild calcRow's input from one /api/profit/calc result (same fields calcProfitForStyles fed the server engine with).
export function rawFromProfit(p, disc) {
  const chSR = {};
  let any = false;
  for (const c of ALL_CH) {
    const cd = (p.channels || {})[c];
    if (cd && cd.actual_sales_qty != null) { chSR[c] = { salesQty: cd.actual_sales_qty, returnQty: cd.actual_return_qty || 0 }; any = true; }
  }
  return { _cat: normCat(p.department || ""), _cp: Number(p.cp_rate) || 0, _rsp: Number(p.rsp) || 0, _disc: disc, _styleReturnPct: 0, _salesQty: 0, ...(any ? { _chSalesReturn: chSR } : {}) };
}

// A copy of the profit result re-priced at `disc` %, same shape as the server's result (channels carry sp / sp_no_tax / gst).
export function whatIfProfit(p, settings, disc) {
  const calc = calcRow(rawFromProfit(p, disc), settings);
  const channels = {};
  for (const c of ALL_CH) {
    if (!(p.channels || {})[c]) continue;
    channels[c] = { ...calc._ch[c], sp: calc._sp, sp_no_tax: calc._spNoTax, gst: calc._gst, actual_sales_qty: p.channels[c].actual_sales_qty, actual_return_qty: p.channels[c].actual_return_qty };
  }
  return { ...p, discount_pct: disc, channels };
}

// Return rate (0..1) per channel from the window's charges_breakdown ({ channel, sale_qty, return_qty }).
// own channel (>= 10 sold) -> style average across channels (>= 10 sold) -> the channel's configured return slab (cd.productReturnPct).
export function returnRates(breakdown, channels) {
  const rows = breakdown || [];
  const byCh = {};
  let sold = 0, ret = 0;
  for (const r of rows) {
    const s = Math.abs(Number(r.sale_qty) || 0), x = Math.abs(Number(r.return_qty) || 0);
    byCh[r.channel] = { sold: s, returned: x };
    sold += s; ret += x;
  }
  const out = {};
  for (const c of Object.keys(channels || {})) {
    const own = byCh[c];
    if (own && own.sold >= MIN_SOLD_FOR_OWN_RATE) out[c] = { r: Math.min(1, own.returned / own.sold), source: "channel", sold: own.sold, returned: own.returned };
    else if (sold >= MIN_SOLD_FOR_OWN_RATE) out[c] = { r: Math.min(1, ret / sold), source: "style", sold, returned: ret };
    else out[c] = { r: Math.min(1, Math.max(0, (Number(channels[c].productReturnPct) || 0) / 100)), source: "slab", sold: 0, returned: 0 };
  }
  return out;
}

// Net profit per piece DISPATCHED = F - r x L.
//   F = forward profit if the piece is kept = SP ex-tax - CP - commission - forward charges (GT + fixed fee, or freight). The engine's own
//       expected-return cost (cd.returnCost / cd.rlCostPerUnit) is taken OUT because r replaces that assumption.
//   L = profit given up when a piece comes back = SP ex-tax - CP - commission (refunded) + reverse logistics
//       (revenue and cost are reversed, commission is refunded, GT / fixed fee / forward freight are kept, RL is added).
// A returned piece therefore earns -(forward charges excl. commission) - RL = F - L.
export function netPerDispatched(cd, cp, r) {
  const complex = cd.gt !== undefined;
  const comm = complex ? cd.comm : cd.commAmt;
  const rl = complex ? cd.weightedCharge : (cd.chargeFreightOnReturn ? cd.freightAmt : 0);
  const F = cd.profit + (complex ? cd.rlCostPerUnit : cd.returnCost);
  const L = cd.sp_no_tax - cp - comm + rl;
  return { net: F - r * L, forward: F, loss: L, rl, comm, r };
}

export function suggestedDiscountFor(p, settings, target = SUGGEST_TARGET_PCT, ch = SUGGEST_CHANNEL) {
  if (!p || !p.found_in_style_master || !settings || !(p.channels || {})[ch]) return null;
  return suggestDiscount(rawFromProfit(p, Number(p.discount_pct) || 0), settings, target, ch);
}
